'use client';

import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/back-button';

/**
 * The route-level error boundary for the whole app.
 *
 * WHY THIS EXISTS. There was no error boundary anywhere: no error.tsx, no
 * global-error.tsx, and no componentDidCatch in any component. So an uncaught
 * render exception on ANY route fell through to Next's built-in fallback, which
 * in a production build is an unstyled white page reading "Application error: a
 * client-side exception has occurred (see the browser console for more
 * information)". No header, no nav, no link back, no branding, and on a
 * marketplace taking real money that page is reachable from the product page and
 * the checkout. not-found.tsx was already written and branded, so the gap was an
 * oversight rather than a position.
 *
 * WHAT IT CATCHES, AND WHAT IT DOES NOT. Placed at src/app/error.tsx it wraps
 * every route segment beneath it, but NOT the root layout itself; a throw in
 * the layout or the providers escapes to global-error.tsx next to this file.
 * That is why both exist: this one keeps the chrome (it renders inside the
 * layout, so the header, footer and theme are still there), and the global one
 * has to re-establish the document because the layout is the thing that failed.
 *
 * `reset()` re-renders the segment without a full navigation, which genuinely
 * recovers the transient cases (a Firestore listener that threw once, a chunk
 * that failed to load on a flaky connection), so it is offered first. The link
 * home is the escape hatch for the rest.
 *
 * DELIBERATELY SAYS NOTHING SPECIFIC. `error.message` is not rendered. In
 * production Next already replaces it with a generic string for server errors,
 * but a client-side throw carries the real message, and that can name internal
 * fields or ids. The digest is shown instead: it is the id that correlates with
 * the server log, which is the only part a visitor reporting the fault needs.
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surfaced to the browser console and, in the App Hosting runtime, to Cloud
    // Logging via the platform's console capture. Logged in an effect rather
    // than during render so a StrictMode double-render does not double-log.
    console.error('[app/error] uncaught render error', error);
  }, [error]);

  return (
    <div className="flex-1 w-full max-w-md mx-auto flex flex-col justify-center px-6 py-20 sm:py-28">
      <div className="rounded-xl border border-border/60 bg-card p-8 sm:p-10 space-y-5">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Error</p>
        <h1>Something went wrong</h1>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
          This page didn’t load properly. Trying again usually fixes it. If it keeps
          happening, let us know at{' '}
          <a href="mailto:support@supa.place" className="text-link underline decoration-link/40 underline-offset-4 hover:decoration-link">
            support@supa.place
          </a>
          .
        </p>
        {/* Nothing a buyer paid for is lost here: purchases are delivered from
            the library and the emailed receipt, neither of which depends on this
            page rendering. Said plainly because an error page mid-checkout is
            exactly where someone assumes the worst. */}
        <p className="text-sm text-muted-foreground leading-relaxed">
          If you were part-way through a purchase, nothing has been charged twice. Check
          your email for a receipt before trying again.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={reset}>Try again</Button>
          <BackButton href="/" label="Back to home" size="default" />
        </div>
        {error.digest && (
          <p className="text-xs text-muted-foreground">
            Reference: <span className="font-mono">{error.digest}</span>
          </p>
        )}
      </div>
    </div>
  );
}
